import React, { useEffect, useState } from "react";
import { Modal, Button, Form, Row, Col } from "react-bootstrap";
import { addData, updateData } from "../../Utility/API";
import { endPoints } from "../../constant/Environment";
import { ErrorToast, SuccesToast } from "../../components/Toast/message";

// datetime-local wants "yyyy-MM-ddTHH:mm" in the admin's own timezone. The API
// returns ISO-8601 carrying the server offset, so convert both ways rather than
// slicing the string, or a survey scheduled in one timezone opens at the wrong
// moment.
const toLocalInput = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(
    d.getHours()
  )}:${pad(d.getMinutes())}`;
};

const fromLocalInput = (value) => {
  if (!value) return null;
  const d = new Date(value);
  return isNaN(d.getTime()) ? null : d.toISOString();
};

const EMPTY = {
  title: "",
  description: "",
  question: "",
  option1: "",
  option2: "",
  option3: "",
  option4: "",
  rewardType: "NONE",
  rewardValue: 0,
  startDate: "",
  endDate: "",
  active: true,
};

const CreateSurvey = ({ show, onHide, survey, onSaved }) => {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (survey) {
      setForm({
        ...EMPTY,
        ...survey,
        rewardValue: survey.rewardValue ?? 0,
        startDate: toLocalInput(survey.startDate),
        endDate: toLocalInput(survey.endDate),
        active: survey.active !== false,
      });
    } else {
      setForm(EMPTY);
    }
  }, [survey, show]);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const save = async () => {
    if (!form.question?.trim() || !form.option1?.trim() || !form.option2?.trim()) {
      ErrorToast("Question and the first two options are required.");
      return;
    }
    if (form.rewardType !== "NONE" && Number(form.rewardValue) <= 0) {
      ErrorToast("Enter a reward amount, or set the reward to None.");
      return;
    }
    // A window that ends before it starts can never satisfy the active-survey
    // query, so the survey would sit in this list marked Active and simply
    // never be served to anyone.
    if (form.startDate && form.endDate) {
      const start = new Date(form.startDate);
      const end = new Date(form.endDate);
      if (end <= start) {
        ErrorToast("The end date must be after the start date.");
        return;
      }
    }
    const payload = {
      ...form,
      id: survey?.id,
      rewardValue: Number(form.rewardValue) || 0,
      startDate: fromLocalInput(form.startDate),
      endDate: fromLocalInput(form.endDate),
    };
    setSaving(true);
    try {
      const res = survey?.id
        ? await updateData(endPoints.api.SURVEY_UPDATE, payload)
        : await addData(endPoints.api.SURVEY_ADD, payload);
      const body = res?.data || res;
      if (body?.status === "success" || body?.status === "SUCCESS") {
        SuccesToast(body?.message || "Survey saved.");
        onSaved && onSaved();
        onHide();
      } else {
        ErrorToast(body?.message || "Could not save the survey.");
      }
    } catch (e) {
      ErrorToast("Could not save the survey.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title>{survey?.id ? "Edit survey" : "Create survey"}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form>
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Title</Form.Label>
                <Form.Control
                  value={form.title || ""}
                  onChange={(e) => set("title", e.target.value)}
                  placeholder="Shown on the promotions card"
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Description</Form.Label>
                <Form.Control
                  value={form.description || ""}
                  onChange={(e) => set("description", e.target.value)}
                  placeholder="Optional subtitle"
                />
              </Form.Group>
            </Col>
          </Row>

          <Form.Group className="mb-3">
            <Form.Label>Question *</Form.Label>
            <Form.Control
              value={form.question || ""}
              onChange={(e) => set("question", e.target.value)}
              placeholder="What the player is asked"
            />
          </Form.Group>

          <Row>
            {[1, 2, 3, 4].map((n) => (
              <Col md={6} key={n}>
                <Form.Group className="mb-3">
                  <Form.Label>
                    Option {n} {n <= 2 ? "*" : "(optional)"}
                  </Form.Label>
                  <Form.Control
                    value={form[`option${n}`] || ""}
                    onChange={(e) => set(`option${n}`, e.target.value)}
                  />
                </Form.Group>
              </Col>
            ))}
          </Row>

          <Row>
            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label>Reward type</Form.Label>
                <Form.Select
                  value={form.rewardType || "NONE"}
                  onChange={(e) => set("rewardType", e.target.value)}
                >
                  <option value="NONE">None</option>
                  <option value="CASH">Cash</option>
                  <option value="BONUS">Bonus</option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label>Reward amount (USD)</Form.Label>
                <Form.Control
                  type="number"
                  min="0"
                  step="0.01"
                  disabled={form.rewardType === "NONE"}
                  value={form.rewardValue ?? 0}
                  onChange={(e) => set("rewardValue", e.target.value)}
                />
                <Form.Text muted>Converted to the player's currency on credit.</Form.Text>
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label>Status</Form.Label>
                <Form.Check
                  type="switch"
                  label={form.active ? "Active" : "Inactive"}
                  checked={!!form.active}
                  onChange={(e) => set("active", e.target.checked)}
                />
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Start</Form.Label>
                <Form.Control
                  type="datetime-local"
                  value={form.startDate || ""}
                  onChange={(e) => set("startDate", e.target.value)}
                />
                <Form.Text muted>Leave blank to start immediately.</Form.Text>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>End</Form.Label>
                {/* min stops the picker offering anything before the start;
                    save() re-checks, since min alone can be typed past. */}
                <Form.Control
                  type="datetime-local"
                  min={form.startDate || undefined}
                  value={form.endDate || ""}
                  onChange={(e) => set("endDate", e.target.value)}
                />
                <Form.Text muted>Leave blank for no expiry.</Form.Text>
              </Form.Group>
            </Col>
          </Row>

          {/* Icon URL is intentionally not exposed: the promotions tile takes
              its image from the `survey` configurations row, and having two
              places to set one image with invisible precedence caused
              confusion on Play. The column is kept for a future per-survey case. */}
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide} disabled={saving}>
          Cancel
        </Button>
        <Button variant="primary" onClick={save} disabled={saving}>
          {saving ? "Saving..." : "Save"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default CreateSurvey;
